import {
  Button,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiGrid } from "react-icons/fi";
import {
  SourcingEventStatus,
  type SourcingEventTemplateDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
  useListSourcingEventTemplatesQuery,
} from "../../../../../../autogen/bff-api";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { templateApplyPatch } from "../../../../../../common/library/applyTemplate";
import type { TemplateSection } from "../../../../../../common/library/templateFromEvent";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import {
  EMPTY_SELECTION,
  pickFromTemplate,
  sectionsDefinedBy,
  selectWholeSections,
  type TemplateSelection,
} from "../../../../../../common/library/templateSelection";
import { appliedSummary } from "./appliedSummary";
import { TemplatePickerStep } from "./TemplatePickerStep";
import { TemplatePreviewStep } from "./TemplatePreviewStep";
import { useSectionStepNumbers } from "./sectionSteps";

// t('Apply template') t('The template «{{name}}» is in — {{summary}}')

interface Props {
  eventId: string;
  preselect: TemplateSection[];
}

export const ApplyTemplateButton = ({ eventId, preselect }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { state, updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  // After announcement the competition documents are binding (FOA § 8-4 / § 18-1).
  const isLocked = state.status !== SourcingEventStatus.Draft;
  const { data, isLoading, error } = useListSourcingEventTemplatesQuery({}, { skip: !isOpen });
  const { trigger } = useApiError();
  const searchRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<SourcingEventTemplateDto | null>(null);
  const [selection, setSelection] = useState<TemplateSelection>(EMPTY_SELECTION);
  const stepNumbers = useSectionStepNumbers(state, picked ? sectionsDefinedBy(picked.content) : []);

  useEffect(() => {
    if (error) trigger(error);
  }, [error, trigger]);

  const close = () => {
    onClose();
    setPicked(null);
    setSelection(EMPTY_SELECTION);
  };

  const pick = (template: SourcingEventTemplateDto) => {
    setPicked(template);
    setSelection(selectWholeSections(template.content, preselect));
  };

  const apply = () => {
    if (!picked) return;
    const chosen = pickFromTemplate(picked.content, selection);
    const order = sectionsDefinedBy(picked.content)
      .filter((section) => stepNumbers[section] != null)
      .sort((a, b) => (stepNumbers[a] ?? 0) - (stepNumbers[b] ?? 0));
    updateField({ eventData: templateApplyPatch(chosen, state.eventData) });
    close();
    toast({
      description: t("The template «{{name}}» is in — {{summary}}", {
        name: picked.name,
        summary: appliedSummary(chosen, order, t, terms),
      }),
      status: "success",
      variant: "subtle",
      duration: 6000,
      isClosable: true,
    });
  };

  return (
    <>
      <Button
        size="sm"
        colorScheme="teal"
        leftIcon={<Icon as={FiGrid} />}
        onClick={onOpen}
        isDisabled={isLocked}
        flexShrink={0}
      >
        {t("Apply template")}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={close}
        size="2xl"
        scrollBehavior="inside"
        isCentered
        initialFocusRef={searchRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          {picked ? (
            <TemplatePreviewStep
              template={picked}
              eventData={state.eventData}
              stepNumbers={stepNumbers}
              selection={selection}
              onSelectionChange={setSelection}
              onBack={() => setPicked(null)}
              onApply={apply}
            />
          ) : (
            <TemplatePickerStep
              templates={data?.templates ?? []}
              isLoading={isLoading}
              searchRef={searchRef}
              onPick={pick}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
